import { createFileRoute, stripSearchParams } from '@tanstack/react-router';
import { hasScope, requireScope } from '~/auth/utils';
import {
    myShiftsQuery,
    recurringShiftsQuery,
    shiftsQuery,
} from '~/util/queries/staff/shifts';
import { ShiftsPlanning } from './-ShiftsPlanning';
import { useScopes } from '~/components/Providers/AuthProvider';
import { Button } from '~/primitives/button';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { NewRecurringShiftSheet } from './-NewRecurringShiftSheet';
import { ScrollArea } from '~/primitives/scroll-area';
import TabSwitch from '~/partials/TabSwitch';
import { TabsContent } from '~/primitives/tabs';
import { defaultShiftsParams, shiftsParamsSchema } from './-SearchParams';
import { ShiftsFilters } from './-Filters';
import { ShiftsTable } from './-ShiftsTable';
import { NewInstantShift } from './-NewInstantShift';

export const Route = createFileRoute('/_authenticated/staff/shifts')({
    validateSearch: shiftsParamsSchema,
    search: {
        middlewares: [stripSearchParams(defaultShiftsParams)],
    },
    loaderDeps: ({ search: { page, per_page, user, status } }) => ({
        page,
        per_page,
        user,
        status,
    }),
    beforeLoad: ({ context: { auth } }) => {
        requireScope(auth, 'submission_review');
    },
    loader: async ({ context: { queryClient, auth }, deps: filters }) => {
        const [recurring, shifts] = await Promise.all([
            hasScope(auth, 'submission_reviewer_visible')
                ? queryClient.fetchQuery(recurringShiftsQuery())
                : [],
            queryClient.fetchQuery(
                hasScope(auth, 'shift_manage')
                    ? shiftsQuery(filters)
                    : myShiftsQuery(filters),
            ),
        ]);
        return { recurring, shifts };
    },
    component: RouteComponent,
    pendingMs: 3000,
});

function RouteComponent() {
    const { hasScope } = useScopes();
    const showAll = hasScope('shift_manage');

    const tab = Route.useSearch({ select: (s) => s.tab });
    const navigate = Route.useNavigate();

    const [newRecurringShiftSheetOpen, setNewRecurringShiftSheetOpen] =
        useState(false);

    const [newInstantShiftSheetOpen, setNewInstantShiftSheetOpen] =
        useState(false);
    return (
        <>
            <main className="h-full flex justify-center">
                <div className="w-full h-full bg-background-dark rounded-2xl py-4">
                    <ScrollArea className="w-full p-4 lg:h-[calc(100vh-150px)]">
                        <div className="flex flex-col gap-4">
                            {hasScope('submission_reviewer_visible') ? (
                                <>
                                    <h1 className="text-secondary-foreground">
                                        Shifts
                                    </h1>

                                    <TabSwitch
                                        current={tab}
                                        options={['planning', 'all']}
                                        optionsDisplay={[
                                            'planning',
                                            showAll
                                                ? 'All Shifts'
                                                : 'My Previous Shifts',
                                        ]}
                                        onChange={(value) =>
                                            navigate({
                                                search: (prev) => ({
                                                    ...prev,
                                                    tab: value,
                                                }),
                                            })
                                        }
                                    >
                                        <TabsContent value="planning">
                                            {(hasScope('shift_create_own') ||
                                                showAll) && (
                                                <Button
                                                    onClick={() =>
                                                        setNewRecurringShiftSheetOpen(
                                                            true,
                                                        )
                                                    }
                                                    className="w-fit"
                                                >
                                                    <Plus className="mr-2" />
                                                    Add planned shift
                                                </Button>
                                            )}
                                            <ShiftsPlanning />
                                            <NewRecurringShiftSheet
                                                open={
                                                    newRecurringShiftSheetOpen
                                                }
                                                onOpenChange={
                                                    setNewRecurringShiftSheetOpen
                                                }
                                                isOwnShift={!showAll}
                                            />
                                        </TabsContent>
                                        <TabsContent value="all">
                                            <div className="flex flex-col gap-4">
                                                <div className="flex-row gap-1">
                                                    {showAll && (
                                                        <ShiftsFilters />
                                                    )}
                                                    <Button
                                                        onClick={() =>
                                                            setNewInstantShiftSheetOpen(
                                                                true,
                                                            )
                                                        }
                                                        className="w-fit sm:hidden"
                                                    >
                                                        <Plus className="mr-2" />
                                                        Start shift
                                                    </Button>
                                                </div>

                                                <ShiftsTable
                                                    setNewInstantShiftSheetOpen={
                                                        setNewInstantShiftSheetOpen
                                                    }
                                                />
                                            </div>
                                        </TabsContent>
                                    </TabSwitch>
                                </>
                            ) : (
                                <>
                                    <h1 className="text-secondary-foreground">
                                        My Previous Shifts
                                    </h1>
                                    <ShiftsTable
                                        setNewInstantShiftSheetOpen={
                                            setNewInstantShiftSheetOpen
                                        }
                                    />
                                </>
                            )}
                        </div>
                    </ScrollArea>
                </div>
            </main>

            <NewInstantShift
                open={newInstantShiftSheetOpen}
                onOpenChange={setNewInstantShiftSheetOpen}
            />
        </>
    );
}
